/**
 * Constantes métier partagées.
 *
 * Source de vérité pour les codes d'assureurs, garanties, statuts, etc.
 * Synchronisé avec les ENUMs SQL et les valeurs UI.
 */

// =============================================================================
// Assureurs supportés
// =============================================================================

export const INSURERS = [
  { code: "beneva", name: "Beneva", short: "BNV" },
  { code: "sunlife", name: "Sun Life", short: "SUN" },
  { code: "manuvie", name: "Manuvie", short: "MAN" },
  { code: "desjardins", name: "Desjardins Assurances", short: "DSJ" },
  { code: "canada-vie", name: "Canada Vie", short: "CVI" },
  { code: "industrielle-alliance", name: "Industrielle Alliance", short: "IA" },
  { code: "ssq", name: "SSQ Assurance", short: "SSQ" },
  { code: "empire-vie", name: "Empire Vie", short: "EMP" },
] as const;

export type InsurerCode = (typeof INSURERS)[number]["code"];

export const INSURER_CODES: readonly InsurerCode[] = INSURERS.map((i) => i.code);

// =============================================================================
// Garanties (couvertures d'assurance collective)
// =============================================================================

export const GUARANTEES = [
  // Assurance vie
  { code: "vie_base", group: "vie", label_fr: "Vie de base", label_en: "Basic life" },
  {
    code: "vie_facultative",
    group: "vie",
    label_fr: "Vie facultative",
    label_en: "Optional life",
  },
  {
    code: "vie_personnes_charge",
    group: "vie",
    label_fr: "Vie personnes à charge",
    label_en: "Dependent life",
  },
  { code: "dma", group: "vie", label_fr: "DMA (Décès & mutilation accidentels)", label_en: "AD&D" },

  // Maladie
  { code: "amc_total", group: "amc", label_fr: "AMC — Total", label_en: "EHC — Total" },
  { code: "amc_medicaments", group: "amc", label_fr: "AMC — Médicaments", label_en: "EHC — Drugs" },
  {
    code: "amc_paramedical",
    group: "amc",
    label_fr: "AMC — Paramédical",
    label_en: "EHC — Paramedical",
  },
  {
    code: "amc_hospitalisation",
    group: "amc",
    label_fr: "AMC — Hospitalisation",
    label_en: "EHC — Hospital",
  },
  {
    code: "amc_voyage",
    group: "amc",
    label_fr: "AMC — Assurance voyage",
    label_en: "EHC — Travel",
  },

  // Dentaire
  {
    code: "dentaire_total",
    group: "dentaire",
    label_fr: "Dentaire — Total",
    label_en: "Dental — Total",
  },
  {
    code: "dentaire_preventif",
    group: "dentaire",
    label_fr: "Dentaire préventif",
    label_en: "Dental preventive",
  },
  {
    code: "dentaire_basic",
    group: "dentaire",
    label_fr: "Dentaire basique",
    label_en: "Dental basic",
  },
  {
    code: "dentaire_majeur",
    group: "dentaire",
    label_fr: "Dentaire majeur",
    label_en: "Dental major",
  },
  {
    code: "dentaire_orthodontie",
    group: "dentaire",
    label_fr: "Orthodontie",
    label_en: "Orthodontics",
  },

  // Vue
  { code: "vue", group: "vue", label_fr: "Soins de la vue", label_en: "Vision care" },

  // Invalidité
  { code: "ic_court_terme", group: "invalidite", label_fr: "IC — Court terme", label_en: "STD" },
  { code: "ic_long_terme", group: "invalidite", label_fr: "IC — Long terme", label_en: "LTD" },

  // Autres
  {
    code: "pae",
    group: "bien_etre",
    label_fr: "PAE (Programme d'aide aux employés)",
    label_en: "EAP",
  },
  { code: "telemedecine", group: "bien_etre", label_fr: "Télémédecine", label_en: "Telemedicine" },
  { code: "maladie_grave", group: "vie", label_fr: "Maladie grave", label_en: "Critical illness" },
] as const;

export type GuaranteeCode = (typeof GUARANTEES)[number]["code"];
export type GuaranteeGroup = (typeof GUARANTEES)[number]["group"];

// =============================================================================
// Rôles utilisateurs (miroir de app.user_role)
// =============================================================================

export const USER_ROLES = ["super_admin", "tenant_admin", "broker", "client_contact"] as const;
export type UserRole = (typeof USER_ROLES)[number];

// =============================================================================
// Statuts de projet (miroir de app.project_status)
// =============================================================================

export const PROJECT_STATUSES = [
  "draft",
  "collecting",
  "ingesting",
  "analyzing",
  "reporting",
  "sent",
  "approved",
  "archived",
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const SUBMISSION_STATUSES = [
  "pending",
  "received",
  "extracting",
  "extracted",
  "reviewed",
  "flagged",
  "ready",
] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export const SEVERITIES = ["info", "warn", "high", "critical"] as const;
export type Severity = (typeof SEVERITIES)[number];

// =============================================================================
// API key scopes (miroir de app.api_key_scope)
// =============================================================================

export const API_KEY_SCOPES = [
  "projects:read",
  "projects:write",
  "companies:read",
  "companies:write",
  "submissions:read",
  "submissions:write",
  "extractions:read",
  "extractions:write",
  "reports:read",
  "reports:write",
  "admin:read",
  "admin:write",
  // Chunk 9.H.2 — read-only retention/legal-hold/sensitive-data foundation.
  // PO-Q9 Option C : délibérément hors des bundles `mcp:read`/`mcp:write`, et il y
  // reste — c'est de la donnée de conformité et de gouvernance légale, pas du métier
  // courtage.
  // MàJ 9.Z.2 : la gouvernance d'attribution, laissée ouverte en 9.H.2, est tranchée.
  // Ce scope est désormais délivrable par le bundle **dédié** `mcp:retention`
  // (cf. `schemas/api-keys.ts`), attribué explicitement et au cas par cas. Avant ce
  // chunk, aucune clé ne pouvait le porter via le flux normal, et les 10 outils MCP
  // de la famille renvoyaient donc un 403 à toute clé provisionnée par bundle.
  // Aucun `retention:write` n'existe, ni ici ni dans l'enum DB : les écritures de
  // rétention restent structurellement hors de portée d'une api_key.
  "retention:read",
  // Chunk 9.V.1 — registre assureurs (ADR 0030). `insurers:read` est inclus
  // dans les bundles `mcp:read`/`mcp:write` (lecture référentiel). `insurers:write`
  // n'est dans AUCUN bundle : les écritures structurelles (create/rename/activate)
  // sont réservées tenant_admin/super_admin côté API — cf. routes/insurers.ts.
  "insurers:read",
  "insurers:write",
] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

// =============================================================================
// Provinces canadiennes
// =============================================================================

export const PROVINCES = [
  { code: "QC", fr: "Québec", en: "Quebec" },
  { code: "ON", fr: "Ontario", en: "Ontario" },
  { code: "BC", fr: "Colombie-Britannique", en: "British Columbia" },
  { code: "AB", fr: "Alberta", en: "Alberta" },
  { code: "MB", fr: "Manitoba", en: "Manitoba" },
  { code: "SK", fr: "Saskatchewan", en: "Saskatchewan" },
  { code: "NS", fr: "Nouvelle-Écosse", en: "Nova Scotia" },
  { code: "NB", fr: "Nouveau-Brunswick", en: "New Brunswick" },
  { code: "NL", fr: "Terre-Neuve-et-Labrador", en: "Newfoundland and Labrador" },
  { code: "PE", fr: "Île-du-Prince-Édouard", en: "Prince Edward Island" },
  { code: "NT", fr: "Territoires du Nord-Ouest", en: "Northwest Territories" },
  { code: "NU", fr: "Nunavut", en: "Nunavut" },
  { code: "YT", fr: "Yukon", en: "Yukon" },
] as const;

// =============================================================================
// Limites (synchronisé avec validation Zod)
// =============================================================================

export const LIMITS = {
  PDF_MAX_SIZE_MB: 50,
  EXTRACTED_TEXT_MAX_CHARS: 500_000,
  COMPANY_NAME_MAX: 200,
  PROJECT_TITLE_MAX: 200,
  NOTE_MAX: 5000,
  PASSWORD_MIN: 12,
} as const;
